import React, { useState, useEffect, useCallback } from 'react';
import { eventsApi } from '../services/api';

export default function EventPage({ eventId, onBack }) {
  const [event,      setEvent]      = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [downloading, setDownloading] = useState(null); // index of image being downloaded

  useEffect(() => {
    eventsApi.getOne(eventId)
      .then(d => setEvent(d.event))
      .catch(() => setError('Event not found or no longer available.'))
      .finally(() => setLoading(false));
  }, [eventId]);

  const images = event?.images || [];

  useEffect(() => {
    if (lightboxIdx === null || !images.length) return;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') setLightboxIdx(i => (i + 1) % images.length);
      if (e.key === 'ArrowLeft')  setLightboxIdx(i => (i - 1 + images.length) % images.length);
      if (e.key === 'Escape')     setLightboxIdx(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIdx, images.length]);

  const downloadImage = useCallback(async (url, idx) => {
    setDownloading(idx);
    try {
      const res  = await fetch(url);
      const blob = await res.blob();
      const ext  = blob.type.split('/')[1] || 'jpg';
      const a    = document.createElement('a');
      a.href     = URL.createObjectURL(blob);
      a.download = `${event?.name || 'photo'}-${idx + 1}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (_) {
      // fallback: open in new tab
      window.open(url, '_blank');
    }
    setDownloading(null);
  }, [event]);

  return (
    <div className="evtpage-wrap">
      {/* Header */}
      <div className="evtpage-header">
        <img
          src="https://firebasestorage.googleapis.com/v0/b/daycare-39d32.firebasestorage.app/o/images%2Flogo_la.png?alt=media"
          alt="LittleAura"
          className="evtpage-logo"
        />
      </div>

      <div className="evtpage-body">
        {loading ? (
          <div className="evtpage-center"><div className="spinner" /></div>
        ) : error ? (
          <div className="evtpage-center">
            <span style={{ fontSize: 48 }}>😕</span>
            <p style={{ color: '#6b7280', marginTop: 12 }}>{error}</p>
            <button className="btn btn-outline" style={{ marginTop: 16 }} onClick={onBack}>← Back to Home</button>
          </div>
        ) : (
          <>
            <div className="evtpage-meta">
              <p className="evtpage-school">{event.schoolName}</p>
              <h1 className="evtpage-title">🎉 {event.name}</h1>
              <p className="evtpage-date">📅 {event.date} · {images.length} photo{images.length !== 1 ? 's' : ''}</p>
            </div>

            {images.length === 0 ? (
              <div className="evtpage-center" style={{ marginTop: 40 }}>
                <span style={{ fontSize: 48 }}>📷</span>
                <p style={{ color: '#9ca3af', marginTop: 8 }}>No photos uploaded yet.</p>
              </div>
            ) : (
              <div className="evtpage-gallery">
                {images.map((img, i) => (
                  <div key={i} className="evtpage-thumb-wrap">
                    <button
                      className="sd-gallery-thumb"
                      onClick={() => setLightboxIdx(i)}
                      aria-label={`View photo ${i + 1}`}
                    >
                      <img src={img.url} alt={`Photo ${i + 1}`} loading="lazy" />
                      <div className="sd-gallery-thumb-overlay">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                          strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
                          <circle cx="11" cy="11" r="8"/>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                          <line x1="11" y1="8" x2="11" y2="14"/>
                          <line x1="8" y1="11" x2="14" y2="11"/>
                        </svg>
                      </div>
                    </button>
                    <button
                      className="evtpage-dl-btn"
                      onClick={() => downloadImage(img.url, i)}
                      disabled={downloading === i}
                      aria-label={`Download photo ${i + 1}`}
                      title="Download"
                    >
                      {downloading === i
                        ? <span className="evtpage-dl-spinner" />
                        : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                            strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                          </svg>
                      }
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="evtpage-footer">
              <button className="btn btn-outline" onClick={onBack}>← Back to LittleAura</button>
            </div>
          </>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && images.length > 0 && (
        <div className="sd-lightbox-overlay" onClick={() => setLightboxIdx(null)}>
          <div className="sd-lightbox-box" onClick={e => e.stopPropagation()}>
            <button className="sd-lightbox-close" onClick={() => setLightboxIdx(null)} aria-label="Close">✕</button>
            {/* Download button in lightbox */}
            <button
              className="evtpage-lb-dl-btn"
              onClick={() => downloadImage(images[lightboxIdx].url, lightboxIdx)}
              disabled={downloading === lightboxIdx}
              title="Download photo"
              aria-label="Download photo"
            >
              {downloading === lightboxIdx
                ? <span className="evtpage-dl-spinner" style={{ borderTopColor: '#fff' }} />
                : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                    strokeLinecap="round" strokeLinejoin="round" width="17" height="17">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
              }
            </button>
            <img
              src={images[lightboxIdx].url}
              alt={`Photo ${lightboxIdx + 1}`}
              className="sd-lightbox-img"
            />
            {images.length > 1 && (
              <>
                <button
                  className="sd-lightbox-nav sd-lightbox-prev"
                  onClick={() => setLightboxIdx(i => (i - 1 + images.length) % images.length)}
                  aria-label="Previous"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
                <button
                  className="sd-lightbox-nav sd-lightbox-next"
                  onClick={() => setLightboxIdx(i => (i + 1) % images.length)}
                  aria-label="Next"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
                <div className="sd-lightbox-counter">{lightboxIdx + 1} / {images.length}</div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
